<template>
	<nav
		:class="['markets-anchor-menu']"
		:style="menuStyle"
	>
		<div class="markets-anchor-menu-container">
			<a
				v-for="item in menu"
				:key="item.label"
				:href="item.href"
				class="block px-4 py-3 font-regular transition markets-anchor-menu-item"
				:class="[
					'text-left',
					{
						'featured-anchor': active === item.label,
						'show-tooltip': item.href === '#coverage' || item.href === '#deep-dives'
					}
				]"
				@click="scrollToAnchor(item.href, item.label)"
				@mouseenter="handleMouseEnter(item)"
				@mouseleave="handleMouseLeave"
			>
				{{ item.label }}
			</a>
		</div>

		<span
			v-if="
				hoveredItem &&
				(hoveredItem.href === '#coverage' || hoveredItem.href === '#deep-dives')
			"
			class="anchor-tooltip"
			:style="tooltipStyle"
		>
			{{ hoveredItem.label }}
		</span>
	</nav>
</template>

<script setup>
import {
	ref,
	computed,
	onMounted,
	onBeforeUnmount,
	watch,
	nextTick
} from "vue";

const menu = [
	{ label: "Featured News", href: "#most-active-header", featured: true },
	{ label: "Markets Coverage", href: "#coverage" },
	{ label: "Most Popular", href: "#popular" },
	{ label: "Deep Dives & Analysis", href: "#deep-dives" },
	{ label: "Market Trends", href: "#trends" },
	{ label: "Trending Topics", href: "#topics" }
];

const active = ref(menu[0].label);
const left = ref("0px");
const hoveredItem = ref(null);
const tooltipStyle = ref({});

const breakpoints = useBreakpoints({
	xl: 1690,
	lg: 1280
});

const isWide = computed(() => breakpoints.isGreater("xl"));
const isNewsPage = computed(() => {
	if (import.meta.server) {
		return false;
	}
	return window?.location?.pathname?.startsWith("/news");
});

const isDesktop = computed(() => breakpoints.isGreater("lg"));

function setActive(label) {
	active.value = label;
}

const menuStyle = computed(() => ({
	top: isNewsPage.value ? (isDesktop.value ? "172px" : "68px") : "36px",
	left: left.value
}));

function updateLeft() {
	if (!isWide.value) {
		left.value = "0px";
		return;
	}
	const container = document.querySelector(`#markets-page`);
	if (container) {
		const rect = container.getBoundingClientRect();
		left.value = rect.left + window.scrollX - 220 + "px";
	} else {
		left.value = "0px";
	}
}

function scrollToAnchor(href, label) {
	setActive(label);
	const id = href.replace("#", "");
	const el = document.getElementById(id);
	if (el) {
		el.scrollIntoView({ behavior: "smooth" });
		history.replaceState(null, "", href);
	}
}

function handleMouseEnter(item) {
	if (window.innerWidth > 1690) {
		return;
	}
	if (item.href === "#coverage" || item.href === "#deep-dives") {
		hoveredItem.value = item;
		// Calculate tooltip position based on the hovered element
		nextTick(() => {
			const hoveredEl = event?.target;
			if (hoveredEl) {
				const rect = hoveredEl.getBoundingClientRect();
				const navEl = document.querySelector(".markets-anchor-menu");
				const navRect = navEl?.getBoundingClientRect();
				if (navRect) {
					tooltipStyle.value = {
						left: `${rect.left - navRect.left + rect.width / 2}px`,
						top: `${rect.bottom - (navRect.top + 8)}px`,
						transform: "translateX(-50%)"
					};
				}
			}
		});
	}
}

function handleMouseLeave() {
	if (window.innerWidth > 1690) {
		return;
	}
	hoveredItem.value = null;
}

// --- Scroll Spy Logic ---

function getSectionOffsets() {
	return menu
		.map((item) => {
			const id = item.href.replace("#", "");
			const el = document.getElementById(id);
			if (!el) {
				return null;
			}
			const rect = el.getBoundingClientRect();
			const top = rect.top + window.scrollY;
			return { label: item.label, top };
		})
		.filter(Boolean);
}

function onScrollSpy() {
	const offsets = getSectionOffsets();
	const scrollPos = window.scrollY + 160; // adjust for sticky header height
	let current = offsets[0]?.label;
	for (let i = 0; i < offsets.length; i++) {
		if (scrollPos >= offsets[i].top) {
			current = offsets[i].label;
		} else {
			break;
		}
	}
	if (current && active.value !== current) {
		active.value = current;
	}
}

onMounted(() => {
	if (isWide.value) {
		updateLeft();
		window.addEventListener("resize", updateLeft);
		window.addEventListener("scroll", updateLeft);
	}
	// Add scroll spy
	nextTick(() => {
		window.addEventListener("scroll", onScrollSpy, { passive: true });
		onScrollSpy(); // initialize
	});
});

onBeforeUnmount(() => {
	window.removeEventListener("resize", updateLeft);
	window.removeEventListener("scroll", updateLeft);
	window.removeEventListener("scroll", onScrollSpy);
});

watch(isWide, (val) => {
	if (val) {
		updateLeft();
		window.addEventListener("resize", updateLeft);
		window.addEventListener("scroll", updateLeft);
	} else {
		window.removeEventListener("resize", updateLeft);
		window.removeEventListener("scroll", updateLeft);
	}
});
</script>

<style scoped lang="scss">
@use "~/assets/utils.scss" as *;
.markets-anchor-menu {
	width: 100vw;
	position: relative;
	left: -20px;
	position: sticky;
	top: 0;
	z-index: 20;
	background-color: white;

	@include breakpoint(lg) {
		background-color: transparent;
	}
	@include breakpoint(xxl) {
		padding-left: 20px;
		padding-right: 20px;
		width: 220px;
		position: fixed;
		left: 0;
		padding-left: 0;
		margin-right: 0;
		z-index: 1;
	}
}

.markets-anchor-menu-container {
	display: flex;
	flex-direction: row;
	overflow-x: auto;
	overflow-y: visible;
	scroll-snap-type: x mandatory;
	-webkit-overflow-scrolling: touch;
	padding-right: 20px;

	&::-webkit-scrollbar {
		display: none;
	}
	scrollbar-width: none;

	@include breakpoint(xxl) {
		flex-direction: column;
		overflow-x: visible;
		padding-right: 0;
	}
}

.featured-anchor {
	position: relative;
	display: block;
	text-align: left;
	padding: 10px 16px;
	background: linear-gradient(90deg, #4f8cff 0%, #b16dff 100%);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
	color: transparent;
	z-index: 1;

	&::before {
		content: "";
		position: absolute;
		inset: 0;
		z-index: -1;
		background: linear-gradient(
			90deg,
			rgba(80, 181, 255, 0.13) 0%,
			rgba(190, 85, 255, 0.13) 100%
		);
	}
}

.markets-anchor-menu-item {
	border: none;
	box-shadow: none;
	color: $black;
	white-space: nowrap;
	scroll-snap-align: start;
	position: relative;
	&:hover:not(.featured-anchor) {
		background: linear-gradient(
			90deg,
			rgba(80, 181, 255, 0.12) 0%,
			rgba(190, 85, 255, 0.12) 99.95%
		);
		border-radius: 4px;
	}

	@include breakpoint(lg) {
		max-width: 152px;
		white-space: nowrap;
		overflow-x: hidden;
		overflow-y: visible;
		&::-webkit-scrollbar {
			display: none;
		}
		scrollbar-width: none;
		text-overflow: ellipsis;
	}
	@include breakpoint(xxl-middle) {
		max-width: 220px;
		white-space: nowrap;
		overflow-x: hidden;
		overflow-y: visible;
		&::-webkit-scrollbar {
			display: none;
		}
		scrollbar-width: none;
		text-overflow: ellipsis;
	}
}

.anchor-tooltip {
	position: absolute;
	white-space: nowrap;
	width: max-content;
	background-color: white;
	padding: 8px 12px;
	border-radius: 8px;
	box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
	font-size: 12px;
	pointer-events: none;
	color: $slate-600;
	z-index: 1000;

	&::before {
		content: "";
		background-image: url("/images/tooltip-carat.svg");
		width: 20px;
		height: 16px;
		position: absolute;
		background-repeat: no-repeat;
		top: -4px;
		left: 50%;
		margin-left: -10px;
	}
}

[id] {
	scroll-margin-top: 124px;
}
</style>
